import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight, Handshake, LineChart, Wrench, ShieldCheck, BarChart3, Store,
  Clock, TrendingUp, Layers, CheckCircle2, MapPin, Globe, Smartphone, Target,
} from "lucide-react";
import { Navbar, Footer } from "@/components/Chrome";
import { ProposalDialog } from "@/components/ProposalDialog";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const HERO_IMG =
  "https://images.unsplash.com/photo-1601598851547-4302969d0614?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const SCREEN_IMG =
  "https://images.unsplash.com/photo-1764795850248-97a5e986b242?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const PHARMA_IMG =
  "https://images.pexels.com/photos/8657367/pexels-photo-8657367.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const MALL_IMG =
  "https://images.unsplash.com/photo-1759323321196-2813db509285?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const PROBLEMS = [
  "Negociar por separado con cada retailer: procesos lentos y repetitivos.",
  "Condiciones comerciales distintas e inventario no consolidado.",
  "Reportes dispersos y dificultad para medir el ROI real.",
];

const SERVICES = [
  { icon: LineChart, t: "Planeación estratégica", d: "Plan anual de Retail Media alineado a tus objetivos de marca y categoría." },
  { icon: Layers, t: "Trading desk de inventario", d: "Compra consolidada de espacios en toda la red de retailers aliados." },
  { icon: Wrench, t: "Producción e instalación POP", d: "Producción, logística e instalación certificada en cada punto de venta." },
  { icon: Store, t: "Trade marketing", d: "Sampling, impulsación y activaciones en el momento de la decisión de compra." },
  { icon: ShieldCheck, t: "Auditoría y control", d: "Verificación en campo con evidencia fotográfica y geolocalizada." },
  { icon: BarChart3, t: "Analytics y BI", d: "Reportería unificada de desempeño y estimación de retorno por retailer." },
];

const STEPS = [
  { n: "01", t: "Define tu objetivo", d: "Cuéntanos tu industria, objetivos de campaña y a quién quieres impactar." },
  { n: "02", t: "Recibe recomendaciones", d: "Nuestro motor prioriza los espacios con mejor ajuste a tu audiencia." },
  { n: "03", t: "Arma tu campaña", d: "Selecciona espacios, servicios y duración; el costo se recalcula al instante." },
  { n: "04", t: "Cierra con un agente", d: "Recibe la cotización por correo o pide contacto inmediato del equipo comercial." },
];

const FAQ = [
  { q: "¿Necesito una cuenta para cotizar?", a: "No necesitas crear una cuenta, pero el cotizador es de acceso restringido: requiere la clave que entrega tu ejecutivo de Spott. Si además creas una cuenta, guardamos tu historial y puedes retomar borradores o duplicar cotizaciones." },
  { q: "¿Cómo obtengo la clave de acceso al cotizador?", a: "Escríbenos a comercial@spott.ec o pídesela a tu ejecutivo asignado. La clave habilita el cotizador en tu dispositivo y es de uso exclusivo para marcas y agencias autorizadas." },
  { q: "¿La cotización es vinculante?", a: "Es una cotización referencial en dólares (USD), válida por 30 días y sujeta a disponibilidad de inventario. El cierre se formaliza con un agente comercial de Spott." },
  { q: "¿Cuál es la duración mínima de una campaña?", a: "Un mes. Puedes extenderla en incrementos mensuales y acceder a descuentos por permanencia desde 3, 6 y 12 meses." },
  { q: "¿Qué incluye el precio de un espacio?", a: "El precio base mensual del espacio en el punto de venta. La producción de material, instalación, desinstalación, artes y auditoría se agregan como servicios adicionales dentro del mismo flujo." },
  { q: "¿Puedo pautar en varios retailers a la vez?", a: "Sí, ese es el corazón de Spott: una sola estrategia, un solo contrato, todos los retailers." },
];

const SERVICE_CHANNELS = [
  { img: "https://images.unsplash.com/photo-1601598851547-4302969d0614?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    t: "Supermercados y tiendas", d: "Cabeceras, islas, POP, sampling e impulsación en el pasillo." },
  { img: "https://images.pexels.com/photos/8657367/pexels-photo-8657367.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    t: "Farmacias", d: "Exhibidores y circuitos digitales de salud y cuidado personal." },
  { img: "https://images.unsplash.com/photo-1759323321196-2813db509285?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    t: "Centros comerciales", d: "LED de gran formato y activaciones BTL en pasillos y plazas internas." },
  { img: "https://images.unsplash.com/photo-1785736098735-51909a463903?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    t: "Plazas y town centers", d: "Espacios abiertos de estilo de vida, gastronomía y entretenimiento." },
  { img: "https://images.unsplash.com/photo-1635873432087-50eba4c4392d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    t: "Transporte masivo", d: "Estaciones, paradas y backlights con frecuencia diaria altísima." },
  { img: "https://images.pexels.com/photos/28575430/pexels-photo-28575430.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    t: "Aeropuertos", d: "Circuitos digitales en llegadas, salidas y salas de embarque." },
];

const Stat = ({ v, l }) => (
  <div>
    <div className="text-2xl font-bold leading-tight text-spott-yellow sm:text-3xl">{v}</div>
    <div className="mt-1 text-xs font-light text-spott-mid sm:text-sm">{l}</div>
  </div>
);

export default function Landing() {
  const [proposalOpen, setProposalOpen] = useState(false);

  useEffect(() => {
    document.title = "Spott — Estrategia, espacios, ejecución y data para tu marca";
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-spott-blue grain">
        <img src={HERO_IMG} alt="Compradores en un supermercado ecuatoriano"
          className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-spott-blue via-spott-blue/95 to-spott-blue/60" />
        <div className="arc-deco -left-32 top-10 h-[420px] w-[420px]" />
        <div className="arc-deco right-10 -bottom-40 h-[520px] w-[520px]" />
        <div className="relative mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-32">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-spott-yellow/40 bg-spott-yellow/10 px-4 py-1.5 text-xs font-medium text-spott-yellow">
              Retail Media Exchange Ecuador
            </span>
            <h1 className="mt-6 text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Una sola estrategia.<br />Un solo contrato.<br />
              <span className="text-spott-yellow">Todos los retailers.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base font-light text-spott-mid md:text-lg">
              Conectamos estrategia, espacios, ejecución y data para llevar tu marca más lejos.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button onClick={() => setProposalOpen(true)} data-testid="hero-cotizar-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-spott-yellow px-7 py-4 text-sm font-bold text-spott-blue transition-transform hover:scale-[1.03]">
                Quiero mi propuesta gratis
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </button>
              <a href="#como-funciona"
                className="rounded-full border border-white/25 px-7 py-4 text-sm font-medium text-white transition-colors hover:border-spott-yellow hover:text-spott-yellow">
                Cómo funciona
              </a>
            </div>
            <div className="mt-16 grid grid-cols-2 gap-8 border-t border-white/10 pt-8 sm:grid-cols-4">
              <Stat v="+5.000" l="Puntos con potencial de activación" />
              <Stat v="+10.000" l="Espacios disponibles" />
              <Stat v="360°" l="Estrategia, ejecución y medición" />
              <Stat v="1 plataforma" l="Para centralizar tu Retail Media" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* PROPUESTA 360 */}
      <section id="propuesta" className="bg-white py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-spott-yellow">Cómo trabajamos</span>
            <h2 className="mt-4 text-2xl font-bold tracking-tight text-spott-blue sm:text-3xl lg:text-4xl">
              Recibe tu propuesta estratégica <span className="text-spott-yellow">GRATIS</span>
            </h2>
            <p className="mt-5 text-sm text-gray-600 md:text-base">
              Cuéntanos tu objetivo y cuánto quieres invertir. Nosotros te mostramos hasta dónde puede
              llegar tu marca. Diseñamos sin costo una propuesta inicial para distribuir tu inversión
              a nivel nacional.
            </p>
            <p className="mt-6 text-base font-semibold text-spott-blue">
              Tú pones el presupuesto. Spott diseña la estrategia.
            </p>
            <button onClick={() => setProposalOpen(true)} data-testid="propuesta-cta-btn"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-spott-yellow px-7 py-4 text-sm font-bold text-spott-blue transition-transform hover:scale-[1.03]">
              Quiero mi propuesta gratis
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
            <p className="mt-3 text-xs text-gray-500">
              Sin compromiso. Déjanos tus datos, objetivo y presupuesto estimado.
            </p>
          </div>
          <div className="grid gap-5 lg:col-span-7 lg:grid-cols-2">
            {[
              { icon: MapPin, t: "Retail & PDV",
                d: "Estrategia de visibilidad y activación en puntos de venta." },
              { icon: Globe, t: "Awareness",
                d: "Espacios de alto impacto, OOH, centros comerciales, aeropuertos y más." },
              { icon: Smartphone, t: "Digital",
                d: "Retail Media digital, CRM, pauta, e-commerce y audiencias." },
              { icon: Target, t: "Estrategia 360°",
                d: "Todo bajo una sola estrategia diseñada según tus objetivos, audiencia y presupuesto." },
            ].map((c, i) => (
              <motion.div key={c.t} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-xl border border-spott-mid bg-spott-light p-7 transition-transform hover:-translate-y-1">
                <c.icon className="text-spott-yellow" size={26} />
                <h3 className="mt-5 text-lg font-semibold text-spott-blue">{c.t}</h3>
                <p className="mt-2 text-sm text-gray-600">{c.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEMA / SOLUCIÓN */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <h2 className="text-2xl font-bold tracking-tight text-spott-blue sm:text-3xl lg:text-4xl">
              Una sola plataforma para todo tu Retail Media
            </h2>
            <p className="mt-5 text-sm text-gray-600 md:text-base">
              Spott centraliza planeación, negociación, compra, ejecución y medición: retail y punto de
              venta, awareness y digital bajo una misma estrategia. Tu equipo deja de perseguir a cada
              retailer y vuelve a enfocarse en la marca.
            </p>
            <ul className="mt-8 space-y-4">
              {PROBLEMS.map((p) => (
                <li key={p} className="flex gap-3 text-sm text-gray-600">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-spott-yellow" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="grid gap-5 lg:col-span-7 lg:grid-cols-2">
            {[
              { icon: Clock, t: "70% menos tiempo", d: "Un solo punto de contacto para toda tu red de retailers." },
              { icon: TrendingUp, t: "20% más retorno", d: "Economías de escala y optimización continua de la mezcla." },
              { icon: Handshake, t: "Un solo contrato", d: "Condiciones comerciales unificadas y negociadas por Spott." },
              { icon: BarChart3, t: "Reportería unificada", d: "Un dashboard con la ejecución real y el impacto de cada dólar." },
            ].map((c, i) => (
              <motion.div key={c.t} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-xl border border-spott-mid bg-spott-light p-7 transition-transform hover:-translate-y-1">
                <c.icon className="text-spott-yellow" size={26} />
                <h3 className="mt-5 text-lg font-semibold text-spott-blue">{c.t}</h3>
                <p className="mt-2 text-sm text-gray-600">{c.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CÓMO FUNCIONA */}
      <section id="como-funciona" className="relative overflow-hidden bg-spott-blue py-20 text-white lg:py-28 grain">
        <div className="arc-deco -right-40 top-1/4 h-[500px] w-[500px]" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-10">
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-spott-yellow">Cómo funciona</span>
          <h2 className="mt-4 max-w-2xl text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            De la idea a la cotización en cuatro pasos
          </h2>
          <div className="mt-14 grid gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-spott-blue p-8">
                <span className="text-4xl font-bold text-spott-yellow/30">{s.n}</span>
                <h3 className="mt-4 text-lg font-semibold">{s.t}</h3>
                <p className="mt-2 text-sm font-light text-spott-mid">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-3">
            {[
              { img: SCREEN_IMG, t: "Pantallas digitales", d: "Circuitos en checkout y entradas de alto tráfico." },
              { img: PHARMA_IMG, t: "Farmacias", d: "Audiencia de salud y cuidado personal, alta frecuencia." },
              { img: MALL_IMG, t: "Centros comerciales", d: "Activaciones BTL y LED de gran formato." },
            ].map((c) => (
              <div key={c.t} className="group overflow-hidden rounded-xl border border-white/10">
                <div className="relative h-48 overflow-hidden">
                  <img src={c.img} alt={c.t}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-spott-blue/40" />
                </div>
                <div className="p-6">
                  <h3 className="text-base font-semibold text-spott-yellow">{c.t}</h3>
                  <p className="mt-1.5 text-sm font-light text-spott-mid">{c.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICIO DE RETAIL MEDIA */}
      <section id="servicio-retail-media" className="bg-white py-20 lg:py-28" data-testid="retail-media-service">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <span className="text-xs font-medium uppercase tracking-[0.2em] text-spott-yellow">
                Servicio de Retail Media
              </span>
              <h2 className="mt-4 text-2xl font-bold leading-tight tracking-tight text-spott-blue sm:text-3xl lg:text-4xl">
                Tu marca en todo el país,<br />no solo en el pasillo
              </h2>
              <p className="mt-5 text-sm text-gray-600 md:text-base">
                Retail Media ya no se limita al supermercado. Spott planifica, compra y ejecuta tu presencia
                en cada punto donde tu audiencia circula, compra y decide: tiendas, farmacias, centros
                comerciales, plazas abiertas, transporte masivo y aeropuertos. Awareness donde hay flujo,
                conversión donde está el producto — con un solo contrato y un solo reporte.
              </p>
              <div className="mt-8 space-y-3">
                {[
                  "Espacios de awareness: LED, DOOH, backlights y activaciones de alto tráfico.",
                  "Espacios de conversión: góndola, islas, exhibidores, sampling e impulsación.",
                  "Cobertura nacional con inventario negociado y auditado por Spott.",
                ].map((x) => (
                  <p key={x} className="flex gap-3 text-sm text-gray-600">
                    <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-spott-yellow" /> {x}
                  </p>
                ))}
              </div>
              <button onClick={() => setProposalOpen(true)} data-testid="service-cotizar-btn"
                className="group mt-9 inline-flex items-center gap-2 rounded-full bg-spott-blue px-7 py-4 text-sm font-bold text-white transition-transform hover:scale-[1.03]">
                Armar mi plan nacional
                <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
              </button>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:col-span-7 lg:grid-cols-3">
              {SERVICE_CHANNELS.map((c, i) => (
                <motion.div key={c.t} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="group overflow-hidden rounded-xl border border-spott-mid bg-spott-light"
                  data-testid={`channel-card-${i}`}>
                  <div className="h-32 overflow-hidden">
                    <img src={c.img} alt={c.t} loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <h3 className="text-sm font-semibold text-spott-blue">{c.t}</h3>
                    <p className="mt-1.5 text-xs text-gray-600">{c.d}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICIOS */}
      <section id="servicios" className="bg-spott-light py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-spott-yellow">Servicios</span>
          <h2 className="mt-4 max-w-2xl text-2xl font-bold tracking-tight text-spott-blue sm:text-3xl lg:text-4xl">
            Todo el ciclo de Retail Media, operado por Spott
          </h2>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <motion.div key={s.t} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }}
                className="rounded-xl border border-spott-mid bg-white p-7 transition-transform hover:-translate-y-1">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-spott-blue">
                  <s.icon size={20} className="text-spott-yellow" />
                </span>
                <h3 className="mt-5 text-lg font-semibold text-spott-blue">{s.t}</h3>
                <p className="mt-2 text-sm text-gray-600">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* RETAILERS */}
      <section id="retailers" className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <div className="max-w-2xl">
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-spott-yellow">Cobertura nacional</span>
            <h2 className="mt-4 text-2xl font-bold tracking-tight text-spott-blue sm:text-3xl lg:text-4xl">
              +15 retailers masivos aliados a nivel nacional
            </h2>
            <p className="mt-5 text-sm text-gray-600 md:text-base">
              Una sola negociación te conecta con las principales cadenas del país y con la audiencia que
              ya está frente al producto, lista para decidir.
            </p>
          </div>

          <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-spott-mid bg-spott-mid sm:grid-cols-2 lg:grid-cols-4"
            data-testid="retailers-grid">
            {[
              { v: "+15", l: "Retailers masivos aliados", d: "Supermercados, farmacias, malls, transporte y aeropuertos." },
              { v: "+10.000", l: "Espacios disponibles", d: "Inventario físico y digital negociado por Spott." },
              { v: "24", l: "Provincias con cobertura", d: "De Quito y Guayaquil a plazas regionales y de proximidad." },
              { v: "+5.000", l: "Puntos de venta activables", d: "Cadenas nacionales, regionales y tiendas de barrio." },
              { v: "+30M", l: "Impactos mensuales estimados", d: "Audiencia acumulada de toda la red aliada." },
              { v: "70%", l: "De las decisiones de compra", d: "Se toman frente a la góndola, no antes." },
              { v: "-70%", l: "Tiempo de gestión", d: "Un solo punto de contacto para toda la red." },
              { v: "360°", l: "Retail, awareness y digital", d: "Una misma estrategia, medida en un solo dashboard." },
            ].map((s, i) => (
              <motion.div key={s.l} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.45, delay: (i % 4) * 0.06 }}
                className="bg-white p-7 transition-colors hover:bg-spott-light">
                <div className="text-3xl font-bold text-spott-yellow">{s.v}</div>
                <h3 className="mt-3 text-base font-semibold text-spott-blue">{s.l}</h3>
                <p className="mt-2 text-sm text-gray-600">{s.d}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-between gap-6 rounded-2xl bg-spott-blue p-8 grain">
            <div>
              <h3 className="text-lg font-semibold text-white md:text-lg">
                Presencia en cada momento del recorrido de compra
              </h3>
              <p className="mt-2 max-w-xl text-sm font-light text-spott-mid">
                Del descubrimiento en la calle y el mall, al pasillo, la góndola y el checkout; y de vuelta
                al celular con retail media digital y audiencias.
              </p>
            </div>
            <button onClick={() => setProposalOpen(true)} data-testid="retailers-cta-btn"
              className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-spott-yellow px-7 py-4 text-sm font-bold text-spott-blue transition-transform hover:scale-[1.03]">
              Ver mi alcance potencial
              <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-spott-light py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-4">
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-spott-yellow">Preguntas frecuentes</span>
            <h2 className="mt-4 text-2xl font-bold tracking-tight text-spott-blue sm:text-3xl lg:text-4xl">
              Lo que suelen preguntarnos
            </h2>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="rounded-xl border border-spott-mid bg-white px-6">
              {FAQ.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`} data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left text-base font-semibold text-spott-blue">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-spott-yellow py-20">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-5 lg:flex-row lg:items-center lg:px-10">
          <div>
            <h2 className="max-w-xl text-2xl font-bold tracking-tight text-spott-blue sm:text-3xl lg:text-4xl">
              Tú pones el presupuesto. Spott diseña la estrategia.
            </h2>
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-spott-blue/80">
              {["Propuesta inicial sin costo", "Estrategia 360° nacional", "Sin compromiso"].map((x) => (
                <span key={x} className="flex items-center gap-2"><CheckCircle2 size={16} /> {x}</span>
              ))}
            </div>
          </div>
          <button onClick={() => setProposalOpen(true)} data-testid="cta-cotizar-btn"
            className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-spott-blue px-8 py-4 text-sm font-bold text-white transition-transform hover:scale-[1.03]">
            Quiero mi propuesta gratis
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </section>

      <ProposalDialog open={proposalOpen} onOpenChange={setProposalOpen} />
      <Footer />
    </div>
  );
}
